import { useState, useEffect } from 'react';
import parseSvg from '../../assets/images/parse.svg';
import extract1Svg from '../../assets/images/extract1.svg';
import extract2Svg from '../../assets/images/extract2.svg';
import extract3Svg from '../../assets/images/extract3.svg';
import splitSvg from '../../assets/images/split.svg';
import chunkSvg from '../../assets/images/chunk.svg';

const extractImages = [extract1Svg, extract2Svg, extract3Svg];

const tabs = [
  {
    id: 'parse',
    label: 'Parse',
    altText: 'Document parsing API — convert PDFs and documents to structured markdown',
    description:
      'Parse documents like a human, capturing layout, structure, meaning with high accuracy. Our agentic layer reviews and corrects outputs in real-time for near-perfect results, even on edge cases.',
    image: parseSvg,
  },
  {
    id: 'extract',
    label: 'Extract',
    altText: 'Document extraction API — extract structured JSON from invoices and forms',
    description:
      'Extract structured data directly from documents in your specified schema with precision and type safety. Ensure fields from invoices, forms, finance docs, etc. land exactly where you need them.',
    image: extract1Svg,
  },
  {
    id: 'split',
    label: 'Split',
    altText: 'Document splitting and classification API — split and classify large document volumes',
    description:
      'Split and classify large documents or large volumes of documents using your own logic based on document structure and content.',
    image: splitSvg,
  },
  {
    id: 'chunk',
    label: 'Chunk',
    altText: 'Document chunking API — semantic chunking for RAG pipelines and AI agents',
    description:
      'Chunk large documents into semantic chunks by page, section, heading, and document hierarchy. Optimize your RAG pipelines and agents with context-aware chunking inputs directly.',
    image: chunkSvg,
  },
];

export default function ApiFeatures() {
  const [active, setActive] = useState('parse');
  const [extractIndex, setExtractIndex] = useState(0);

  // Rotate extract images while extract tab is active
  useEffect(() => {
    if (active !== 'extract') return;
    const interval = setInterval(() => {
      setExtractIndex((i) => (i + 1) % extractImages.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [active]);

  useEffect(() => {
    if (active !== 'extract') setExtractIndex(0);
  }, [active]);

  const activeImage =
    active === 'extract'
      ? extractImages[extractIndex]
      : tabs.find((t) => t.id === active)?.image;

  const imageKey = active === 'extract' ? `extract-${extractIndex}` : active;
  const activeIndex = tabs.findIndex((t) => t.id === active);

  return (
    <section className="bg-white">

      {/* ── Mobile ── */}
      <div className="md:hidden flex flex-col bg-white py-10">
        <div className="pt-8 pb-5 text-center px-6">
          <p className="text-[#546FFF] text-sm font-semibold tracking-widest uppercase mb-2">
            Flexible APIs
          </p>
          <h2 className="text-3xl font-bold text-[#080808]">
            APIs to parse, extract, chunk
          </h2>
        </div>

        <div className="flex items-center gap-2 px-6 mb-6">
          {tabs.map((tab) => {
            const isActive = active === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActive(tab.id)}
                className={`flex-1 py-3 rounded-lg text-sm font-semibold transition-all duration-200 ${
                  isActive
                    ? 'bg-[#546FFF] text-white border border-[#546FFF]'
                    : 'bg-white text-[#546FFF] border border-[#D0D5FF] hover:border-[#546FFF]'
                }`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div className="px-6 flex flex-col">
          {tabs.map((tab) =>
            active === tab.id ? (
              <div
                key={tab.id}
                className="flex flex-col"
                style={{ animation: 'apiFadeIn 0.3s ease' }}
              >
                <p className="text-sm text-[#08080899] leading-relaxed mb-4 text-center">
                  {tab.description}
                </p>
                <div className="flex items-center justify-center">
                  <img
                    key={imageKey}
                    src={activeImage}
                    alt={tab.altText}
                    className="w-full object-contain"
                    decoding="async"
                    style={{ animation: 'apiFadeIn 0.35s ease' }}
                  />
                </div>
              </div>
            ) : null
          )}
        </div>

        <div className="px-6 pt-6 pb-2 flex items-center justify-between">
          <a
            href="https://docstrange.nanonets.com/docs"
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm font-semibold text-[#546FFF] hover:text-[#3A4DB2] transition-colors duration-200"
          >
            See API docs →
          </a>
          <span className="text-xs text-[#08080855]">
            {activeIndex + 1} / {tabs.length}
          </span>
        </div>
      </div>

      {/* ── Desktop ── */}
      <div className="hidden md:block bg-white py-14">
        {/* Heading */}
        <div className="max-w-[1200px] mx-auto px-6 w-full pb-10 text-center">
          <p className="text-[#546FFF] text-sm font-semibold tracking-widest uppercase mb-3">
            Flexible APIs
          </p>
          <h2 className="text-3xl md:text-4xl font-bold text-[#080808]">
            APIs to parse, extract, chunk
          </h2>
        </div>

        {/* Accordion + image */}
        <div className="max-w-[1200px] mx-auto px-6 w-full flex flex-row gap-12 items-center pb-12">
          {/* Accordion */}
          <div className="w-[380px] flex-shrink-0 flex flex-col">
            {tabs.map((tab, i) => {
              const isOpen = active === tab.id;
              return (
                <div key={tab.id}>
                  <div
                    className="py-5 cursor-pointer group"
                    onMouseEnter={() => setActive(tab.id)}
                  >
                    <div className="flex items-center justify-between">
                      <span
                        className={`text-xl font-bold transition-colors duration-300 ${
                          isOpen
                            ? 'text-[#546FFF]'
                            : 'text-[#080808] group-hover:text-[#546FFF]'
                        }`}
                      >
                        {tab.label}
                      </span>
                      <svg
                        className={`w-4 h-4 flex-shrink-0 transition-all duration-300 ease-in-out ${
                          isOpen
                            ? 'rotate-180 text-[#546FFF]'
                            : 'rotate-0 text-[#08080866]'
                        }`}
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                    <div
                      className={`grid transition-all duration-300 ease-in-out ${
                        isOpen
                          ? 'grid-rows-[1fr] opacity-100'
                          : 'grid-rows-[0fr] opacity-0'
                      }`}
                    >
                      <div className="overflow-hidden">
                        <div className="mt-3">
                          <p className="text-base text-[#08080899] leading-relaxed">
                            {tab.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                  {i < tabs.length - 1 && <div className="h-px bg-[#F0F0F0]" />}
                </div>
              );
            })}
            <div className="pt-10">
              <a
                href="https://docstrange.nanonets.com/docs"
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#546FFF] hover:text-[#3A4DB2] transition-colors duration-200"
              >
                See API docs →
              </a>
            </div>
          </div>

          {/* Image */}
          <div className="flex-1 flex items-center">
            <img
              key={imageKey}
              src={activeImage}
              alt={tabs.find((t) => t.id === active)?.altText || active}
              className="w-full object-contain"
              decoding="async"
              style={{ animation: 'apiFadeIn 0.35s ease' }}
            />
          </div>
        </div>
      </div>

      <style>{`
        @keyframes apiFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </section>
  );
}
